import { ColumnDef } from '@tanstack/react-table'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useMemo, useState } from 'react'
import { Spinner, Form } from 'react-bootstrap'
import { FiEdit2 } from 'react-icons/fi'
import apiService from 'services/api'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { Switch } from '../../../components/UI/Switch/Switch'
import { MISC_SETTINGS } from '../../../models/misc_settings'
import { MiscSetting } from '../../../models/preferences'
import { queryClient } from '../../../services/queryClient'
import { notifySuccess, notifyError } from '../../../services/toast'
import * as S from './styles'

const description = `Customize your First Bite experience with the following preferences.`

export function MiscSettingsTable() {
  const api = apiService()
  const mutation = api.useChangeMiscSettings()
  const updateReportingUnitMutation = api.useUpdateReportingUnit()
  const { data, isLoading } = api.useGetPreferences()

  const [isApplyingTempClosed, setIsApplyingTempClosed] = useState(false)
  const [reportingUnitValue, setReportingUnitValue] = useState('')
  const [isEditingReportingUnit, setIsEditingReportingUnit] = useState(false)

  async function handleChangeTempClosed(val: boolean) {
    setIsApplyingTempClosed(true)
    await mutation.mutateAsync({
      key: 'show_temp_closed',
      value: val ? '1' : '0',
    })
    setIsApplyingTempClosed(false)
    notifySuccess('Setting Changed Successfully')
    queryClient.clear()
  }

  async function handleUpdateReportingUnit() {
    const trimmedValue = reportingUnitValue.trim()
    const currentValue = data?.reporting_unit || ''

    // Don't save if value hasn't changed
    if (trimmedValue === currentValue) {
      setIsEditingReportingUnit(false)
      return
    }

    if (!trimmedValue) {
      notifyError('Reporting unit cannot be blank')
      setReportingUnitValue(currentValue) // Reset to original value
      setIsEditingReportingUnit(false)
      return
    }

    try {
      await updateReportingUnitMutation.mutateAsync({
        reporting_unit: trimmedValue,
      })
      setIsEditingReportingUnit(false)
      notifySuccess('Reporting Unit Updated Successfully')
      queryClient.invalidateQueries({ queryKey: ['preferences'] })
    } catch (error) {
      notifyError('Failed to update reporting unit')
      setReportingUnitValue(currentValue) // Reset to original value on error
      setIsEditingReportingUnit(false)
    }
  }

  const columns = useMemo<ColumnDef<MiscSetting, any>[]>(
    () => [
      {
        accessorKey: 'key',
        header: 'Setting',
        cell: (info) =>
          MISC_SETTINGS[info.getValue() as keyof typeof MISC_SETTINGS].title,
        size: 150,
      },
      {
        id: 'description',
        accessorKey: 'key',
        header: 'Description',
        cell: (info) =>
          MISC_SETTINGS[info.getValue() as keyof typeof MISC_SETTINGS]
            .description,
        size: 300,
      },
      {
        id: 'edit',
        header: 'Actions',
        size: 70,
        meta: { rightAlign: false },
        cell: ({ row }) => {
          if (row.original.key === 'show_temp_closed') {
            if (isApplyingTempClosed) {
              return <Spinner animation="border" />
            }
            return (
              <div className={'justify-end flex'}>
                <Switch
                  onChange={(e) => handleChangeTempClosed(e.target.checked)}
                  defaultChecked={!!parseInt(row.original.value)}
                />
              </div>
            )
          }
          if (row.original.key === 'reporting_unit') {
            if (isEditingReportingUnit) {
              return (
                <div className={'justify-end flex gap-2 items-center'}>
                  {updateReportingUnitMutation.isPending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <Form.Control
                      type="text"
                      size="sm"
                      style={{ width: '100px' }}
                      value={reportingUnitValue}
                      onChange={(e) => setReportingUnitValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdateReportingUnit()
                        } else if (e.key === 'Escape') {
                          setIsEditingReportingUnit(false)
                          setReportingUnitValue(row.original.value)
                        }
                      }}
                      onBlur={handleUpdateReportingUnit}
                      autoFocus
                    />
                  )}
                </div>
              )
            }
            return (
              <div className={'justify-end flex gap-2 items-center'}>
                <span className="font-bold">{row.original.value}</span>
                <FiEdit2
                  size={20}
                  color="#667085"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setReportingUnitValue(row.original.value)
                    setIsEditingReportingUnit(true)
                  }}
                />
              </div>
            )
          }
          return null
        },
      },
    ],
    [
      data,
      isApplyingTempClosed,
      isEditingReportingUnit,
      reportingUnitValue,
      updateReportingUnitMutation.isPending,
    ]
  )

  // hotfix: exclude items which do not have a description in MISC_SETTINGS
  // Also add reporting_unit as a pseudo-MiscSetting since it's stored on Company model
  const filteredData = useMemo(() => {
    const miscSettings = (data?.misc_settings || []).filter(
      (setting) => MISC_SETTINGS[setting.key as keyof typeof MISC_SETTINGS]
    )

    // Add reporting_unit as a pseudo-setting
    if (data?.reporting_unit) {
      miscSettings.push({
        key: 'reporting_unit' as any,
        value: data.reporting_unit,
      })
    }

    return miscSettings
  }, [data])

  if (isLoading || !data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          width: '100%',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <DataTableContainer>
      <ClientDataTable
        height={'100%'}
        tableHeader={
          <S.SettingsTableHeader>
            <S.TableDescriptionText>
              <OverflownTextTooltip
                tooltipText={description}
                maxLines={4}
                placement="bottom"
              >
                {description}
              </OverflownTextTooltip>
            </S.TableDescriptionText>
          </S.SettingsTableHeader>
        }
        stickyLastColumn={true}
        data={filteredData}
        columns={columns}
      />
    </DataTableContainer>
  )
}
